/**
 * Framework-free filter contract for the taxonomy tour grids — the destination
 * detail page and the activity-type detail page both browse a pre-loaded set of
 * tours through these helpers, so URL state and the rendered grid never drift.
 *
 * URL params:
 *   sale=1                              → "On Sales" tab
 *   type=<activity name>                → matches any activity the tour carries
 *   duration=half|full|short|week|long  (same IDs as /tours ?dur param)
 *
 * One state shape serves both pages so the parse/apply pair can't become two
 * near-copies that drift — the failure mode that left callouts unstyled on one
 * page and fine on the other. The activity axis is only RENDERED on the
 * destination page (an activity-type page is already one activity), but the
 * contract is shared.
 *
 * The activity axis matches on NAME: `styles[]` carries names, and
 * `activitySlug` is optional in the DTO.
 *
 * All filtering is client-side over the already-loaded tour set (each page
 * fetches up to perPage=100 for its facet), so these helpers never touch the
 * network and the pages' ISR caching is untouched.
 */

import type { TourDto } from "@tripcart/shared";
import {
  bandForPackage,
  type DurationBandId,
} from "@tripcart/shared/duration/duration-bands";

import { DURATION_BANDS } from "../tours/_filters-shared";

export { DURATION_BANDS as DURATION_BUCKETS };

export type DurationBucket = DurationBandId;

const DURATION_IDS = new Set<string>(DURATION_BANDS.map((b) => b.id));

export interface TourFilterState {
  sale: boolean;
  activity: string | null;
  duration: DurationBucket | null;
}

export type ParamGetter = (key: string) => string | undefined;

export function parseFilters(get: ParamGetter): TourFilterState {
  const durationRaw = get("duration");
  const activity = get("type")?.trim();
  return {
    sale: get("sale") === "1",
    activity: activity ? activity : null,
    duration: DURATION_IDS.has(durationRaw ?? "")
      ? (durationRaw as DurationBucket)
      : null,
  };
}

export function hasActiveFilters(state: TourFilterState): boolean {
  return state.sale || state.activity !== null || state.duration !== null;
}

/**
 * The duration bands actually represented in the set, shortest first.
 *
 * Offering all five regardless is what produced the dead end on the live
 * Everest page: four of them returned "No tours match" because no tour there
 * has a duration saved. The activity dropdown has always been derived from
 * what is present; this brings duration in line. An empty result means the
 * host should not render the control at all.
 */
export function deriveDurationOptions(
  tours: TourDto[],
): (typeof DURATION_BANDS)[number][] {
  const present = new Set<string>();
  for (const tour of tours) {
    const band = durationBucket(tour);
    if (band) present.add(band);
  }
  return DURATION_BANDS.filter((b) => present.has(b.id));
}

/**
 * Every activity present in the set, alphabetically.
 *
 * Reads `styles[]` — the FULL set a tour is tagged with — and falls back to the
 * singular `activity` only when it's absent (optional in the DTO for legacy
 * fixtures). Deriving from `activity` alone reported just the PRIMARY tag, so a
 * tour tagged both "Trekking & Hiking" and "Day Tour" contributed one option
 * and the other silently never appeared in the filter — a tour you could not
 * reach by the very facet it carries.
 */
export function deriveActivityOptions(tours: TourDto[]): string[] {
  const seen = new Set<string>();
  for (const tour of tours) {
    for (const name of activityNamesOf(tour)) seen.add(name);
  }
  return [...seen].sort((a, b) => a.localeCompare(b));
}

/** The activity names a tour carries — all of its styles, else its primary. */
function activityNamesOf(tour: TourDto): string[] {
  const fromStyles = (tour.styles ?? [])
    .map((s) => s.name?.trim())
    .filter((n): n is string => Boolean(n));
  if (fromStyles.length > 0) return fromStyles;
  const primary = tour.activity?.trim();
  return primary ? [primary] : [];
}

/** A tour is on sale when any package category advertises a struck-through
 *  `was` price above its current price — on a flat price or any tier. */
export function isOnSale(tour: TourDto): boolean {
  return tour.packages.some((pkg) =>
    pkg.categories.some((cat) => {
      if (cat.flat?.was && cat.flat.was.amount > cat.flat.now.amount) {
        return true;
      }
      return (cat.tiers ?? []).some(
        (tier) => tier.was && tier.was.amount > tier.price.amount,
      );
    }),
  );
}

export function anyOnSale(tours: TourDto[]): boolean {
  return tours.some(isOnSale);
}

function defaultPackageOf(
  tour: TourDto,
): TourDto["packages"][number] | undefined {
  return tour.packages.find((p) => p.isDefault) ?? tour.packages[0];
}

/**
 * Maps a tour to a duration band, or null when it can't be classified.
 *
 * Null means "we don't know", and an unknown tour deliberately matches NO
 * band. This used to fall back to `tour.type === "single-day" ? "full" : null`,
 * which read a taxonomy field as a length: traveler-api derives that type as
 * `exp.type === "multi_day" ? "multi-day" : "single-day"`, and virtually every
 * tour is stored as `activity`, so almost everything arrived labelled
 * "single-day" regardless of how long it ran. A 25-day expedition with no
 * duration saved therefore answered the "1 day" filter and disappeared from
 * "8+ days" — a confident wrong answer, which is worse than no answer.
 *
 * Matches how /tours has always behaved: it filters server-side on
 * durationMinMinutes/durationMaxMinutes, so a package with no duration matches
 * no band there either. Same question, same answer, both surfaces.
 */
export function durationBucket(tour: TourDto): DurationBucket | null {
  const pkg = defaultPackageOf(tour);
  return pkg ? (bandForPackage(pkg)?.id ?? null) : null;
}

/** AND-composes the active filters over the loaded tour set. */
export function applyFilters(
  tours: TourDto[],
  state: TourFilterState,
): TourDto[] {
  return tours.filter((tour) => {
    if (state.sale && !isOnSale(tour)) return false;
    // Matched against every tag the tour carries, not just its primary — the
    // same basis the options are derived from. Comparing to `tour.activity`
    // alone meant picking a non-primary option returned an empty grid.
    if (
      state.activity !== null &&
      !activityNamesOf(tour).includes(state.activity)
    ) {
      return false;
    }
    if (state.duration !== null && durationBucket(tour) !== state.duration) {
      return false;
    }
    return true;
  });
}
